import {Component, inject, Input, OnInit} from '@angular/core';
import {EditorComponent} from "ngx-monaco-editor-v2";
import {FormsModule} from "@angular/forms";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {Location, NgOptimizedImage, UpperCasePipe} from "@angular/common";
import {QueryDataService} from "../services/query-data.service";
import {ActivatedRoute, NavigationExtras, Router} from "@angular/router";
import {ConfigRouteEnum} from "../../../shared/enums/config-route.enum";
import {
    QueryBody,
    QueryValidation,
    SqlAgentResponse,
} from "../../../shared/services/models/query-service.model";
import {DetailHeaderComponent} from "../../../shared/components/config-detail/detail-header/detail-header.component";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {ConfigurationService} from "../../../shared/services/configuration.service";
import {ConfigDetailComponent} from "../../../shared/components/config-detail/config-detail.component";
import {DetailBodyComponent} from "../../../shared/components/config-detail/detail-body/detail-body.component";
import {CardModule} from "primeng/card";
import {InputTextModule} from "primeng/inputtext";
import {ButtonModule} from "primeng/button";
import {ProgressSpinnerModule} from "primeng/progressspinner";

@Component({
    selector: 'app-query-edit',
    imports: [
        CardModule,
        InputTextModule,
        EditorComponent,
        FormsModule,
        TranslatePipe,
        ButtonModule,
        UpperCasePipe,
        DetailHeaderComponent,
        NgOptimizedImage,
        ProgressSpinnerModule,
        ConfigDetailComponent,
        DetailBodyComponent
    ],
    templateUrl: './query-edit.component.html',
    styleUrl: './query-edit.component.scss'
})
export class QueryEditComponent implements OnInit {
    @Input() navigateBack?: { commands: any[], extras?: NavigationExtras };

    private readonly queryDataService = inject(QueryDataService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly location = inject(Location);
    private readonly snackBar = inject(SnackbarService);
    private readonly translate = inject(TranslateService);
    private readonly config = inject(ConfigurationService).configuration;

    protected readonly ConfigRouteEnum = ConfigRouteEnum;

    logoUrl: string | null = this.config.app.defaultLogo;
    title: string | undefined;
    queryID: number | null = null;
    appName: string = '';
    appVersion: string = '';
    queryName: string = '';
    queryVersion: string = '';
    currentQueryVersion: string = '';
    dialect: string = '';

    agent_id: string = "ag_019bdb501913741a9d855c31fc4e1456";
    role: string = "user"
    aiActive: boolean = false;
    aiLoading: boolean = false;
    aiValid: boolean = false;
    aiNotes: string = '';
    aiError: boolean = true;
    aiErrorMessage: string = '';
    aiErrorCodeFragment: string = '';
    aiWarning: boolean = true;
    aiWarningType: string = '';
    aiWarningMessage: string = '';

    editorOptions = {
        language: 'sql',
        automaticLayout: true,
        minimap: {enabled: false},
    };
    code: string = '';

    ngOnInit() {
        this.subscribeToRoute();
    }

    private subscribeToRoute() {
        this.activatedRoute.paramMap.subscribe(params => {
            const idParam = params.get('id');

            if (idParam && idParam !== 'new') {
                this.queryID = +idParam;
                this.getQueryData();
            } else {
                this.queryID = null;
                this.resetFields();
            }
        });
    }

    private resetFields() {
        this.code = '';
        this.appName = '';
        this.appVersion = '';
        this.queryName = '';
        this.queryVersion = '';
        this.dialect = '';
    }

    private getQueryData() {
        if (this.queryID === null) return;
        this.queryDataService.getQuery(this.queryID).subscribe({
            next: (response: any) => {
                this.code = response.query;
                this.appName = response.application.name;
                this.appVersion = response.application.version;
                this.queryName = response.name;
                this.queryVersion = response.version;
                this.dialect = response.dialect;
                this.currentQueryVersion = this.queryVersion;
            },
            error: (error) => console.error(error)
        })
    }

    saveQuery() {
        const query: QueryBody = {
            name: this.queryName,
            version: this.queryVersion,
            application: {
                name: this.appName,
                version: this.appVersion
            },
            query: this.code,
            dialect: this.dialect
        };

        if (this.queryVersion === this.currentQueryVersion) {
            this.snackBar.Error(this.translate.instant("snackbar.query.version_error"));
            return;
        }

        this.queryDataService.createQuery(query).subscribe({
            next: () => {
                this.router.navigate(['/', ConfigRouteEnum.QUERY])
                this.snackBar.Success(this.translate.instant("snackbar.query.save", { queryName: this.queryName, queryVersion: this.queryVersion }));
            },
            error: (error) => {
                console.error(error)
                this.snackBar.Error(this.translate.instant("snackbar.query.save_error"));
            }
        });
    }

    resetQuery() {
        this.subscribeToRoute();
    }

    validateQuery() {
        this.aiActive = true;
        this.aiLoading = true;
        this.aiWarning = true;
        this.aiError = true;

        const validation: QueryValidation = {
            agent_id: this.agent_id,
            inputs: [
                {
                    role: this.role,
                    content: this.code
                }
            ]
        };

        this.queryDataService.validateQuery(validation).subscribe({
            next: (response: any) => {
                const raw = response?.outputs?.[0]?.content;
                const parsedResponse = JSON.parse(raw);
                this.aiValid = parsedResponse.isValid;
                this.aiNotes = parsedResponse.notes ?? [];
                const error = parsedResponse.errors[0] ?? [];
                const warning = parsedResponse.warnings[0] ?? [];

                if(error.length === 0) {
                    this.aiError = false;
                }
                if(warning.length === 0) {
                    this.aiWarning = false;
                }

                this.aiErrorMessage = error.message;
                this.aiErrorCodeFragment = error.codeFragment;

                this.aiWarningType = warning.type;
                this.aiWarningMessage = warning.message;
                const sqlResult = parsedResponse as SqlAgentResponse;

                if (sqlResult.isValid) {
                    this.snackBar.Success(this.translate.instant("snackbar.query.validated_success"));
                } else {
                    this.snackBar.Error(this.translate.instant("snackbar.query.validated_error"));
                }
                this.aiLoading = false;
            },
            error: (error) => {
                console.error(error);
                this.snackBar.Error(this.translate.instant("snackbar.query.validation_error"));
                this.aiLoading = false;
            }
        });
    }
}
